import React, { useEffect, useState } from 'react';
import { AuthAPI } from '../../Apis/AuthAPI';

export default function Callback() {
  const [callbackData, setCallbackData] = useState({
    urlParams: {},
    rawSamlResponse: null,
    decodedSaml: null,
    error: null,
    processingState: 'Inizializzazione...'
  });

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Recupera i parametri dall'URL
        const urlParams = new URLSearchParams(window.location.search);
        
        // Salva tutti i parametri URL per debug
        const allParams = {};
        for (const [key, value] of urlParams.entries()) {
          allParams[key] = value;
        }
        
        setCallbackData(prev => ({ 
          ...prev, 
          urlParams: allParams,
          processingState: 'Parametri URL recuperati'
        }));
        
        // Se c'è una SAMLResponse, decodificala e visualizzala
        const samlResponse = urlParams.get('SAMLResponse');
        if (samlResponse) {
          try {
            const decodedSaml = atob(samlResponse);
            setCallbackData(prev => ({ 
              ...prev, 
              rawSamlResponse: samlResponse,
              decodedSaml: decodedSaml,
              processingState: 'Risposta SAML decodificata'
            }));
          } catch (e) {
            console.error('Errore nella decodifica SAML:', e);
            setCallbackData(prev => ({ 
              ...prev, 
              error: `Errore nella decodifica SAML: ${e.message}`,
              processingState: 'Errore decodifica SAML'
            }));
          }
        }
        
        // Controlla se ci sono errori nell'URL
        const error = urlParams.get('error');
        const errorDescription = urlParams.get('error_description');
        if (error || errorDescription) {
          setCallbackData(prev => ({ 
            ...prev, 
            error: errorDescription || error,
            processingState: 'Errore rilevato nei parametri URL'
          }));
          return; // Non continuare con l'autenticazione
        }
        
        // Procedi con il normale flusso di autenticazione solo se c'è un codice
        const code = urlParams.get('code');
        if (code) {
          // Recupera il code verifier e redirect URI
          const codeVerifier = localStorage.getItem('code_verifier');
          const redirectUri = localStorage.getItem('redirect_uri');
          
          setCallbackData(prev => ({ 
            ...prev,
            processingState: 'Codice di autorizzazione ricevuto, iniziando scambio token'
          }));
          
          if (!codeVerifier || !redirectUri) {
            throw new Error('Code verifier o redirect URI non trovati');
          }
          
          try {
            // Scambia il codice per i token attraverso il backend
            const tokenResponse = await AuthAPI.exchangeToken(code, codeVerifier, redirectUri);
            
            setCallbackData(prev => ({ 
              ...prev,
              tokenResponse: tokenResponse.data.data,
              processingState: 'Token ricevuti, recupero profilo utente'
            }));
            
            // Ottieni il profilo utente dal backend
            const profileResponse = await AuthAPI.getProfile(tokenResponse.data.data.access_token);
            
            setCallbackData(prev => ({ 
              ...prev,
              user: profileResponse.data.data.user,
              processingState: 'Autenticazione completata'
            }));
            
            // Salva i dati dell'utente
            localStorage.setItem('user', JSON.stringify(profileResponse.data.data.user));
            localStorage.setItem('access_token', tokenResponse.data.data.access_token);
            if (tokenResponse.data.data.id_token) {
              localStorage.setItem('id_token', tokenResponse.data.data.id_token);
            }
            if (tokenResponse.data.data.refresh_token) {
              localStorage.setItem('refresh_token', tokenResponse.data.data.refresh_token);
            }
            sessionStorage.setItem("isLoggedIn", true);
            
            // Pulizia
            localStorage.removeItem('code_verifier');
            localStorage.removeItem('redirect_uri');
            
            // Redirect alla pagina di destinazione
            setTimeout(() => {
              const destination = localStorage.getItem('destination') || '/';
              localStorage.removeItem('destination');
              window.location.href = destination;
            }, 3000); // Ritardo per dare il tempo di vedere i dati
            
          } catch (tokenError) {
            console.error('Errore nello scambio token:', tokenError);
            setCallbackData(prev => ({ 
              ...prev, 
              error: tokenError.response?.data?.message || tokenError.message,
              processingState: 'Errore nello scambio token'
            }));
          }
        } else if (!samlResponse) { 
          // Se non abbiamo né code né SAMLResponse
          setCallbackData(prev => ({ 
            ...prev,
            error: 'Nessun codice di autorizzazione o risposta SAML ricevuti',
            processingState: 'Dati mancanti'
          }));
        }
      } catch (error) {
        console.error('Errore generale:', error);
        setCallbackData(prev => ({ 
          ...prev, 
          error: error.message,
          processingState: 'Errore generale'
        }));
      }
    };

    handleCallback();
  }, []);

  // Funzione per tornare alla pagina di login
  const handleBackToLogin = () => {
    window.location.href = '/login-spid';
  };

  return (
    <div className="container mt-5">
      <div className="row justify-content-center">
        <div className="col-12 col-md-8 text-center">
          {/* Logo SPID */}
          <img 
            src="../assets/images/spid-agid-logo-lb.png" 
            alt="Logo SPID" 
            style={{ 
              maxWidth: '200px', 
              marginBottom: '2rem' 
            }} 
          />

          <div className="card shadow-sm">
            <div className="card-body p-4">
              <h1 className="h3 mb-4">
                Autenticazione in corso
              </h1>
              
              {/* Loading Spinner */}
              {!callbackData.error && !callbackData.user && (
                <div className="my-4">
                  <div className="progress" style={{ height: '4px' }}>
                    <div 
                      className="progress-bar progress-bar-striped progress-bar-animated" 
                      role="progressbar" 
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className="mt-3">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Caricamento...</span>
                    </div>
                    <p className="text-muted mt-2">{callbackData.processingState}</p>
                  </div>
                </div>
              )}
              
              {/* Errore */}
              {callbackData.error && (
                <div className="alert alert-danger d-flex align-items-center" role="alert">
                  <svg className="icon icon-danger me-2" aria-hidden="true">
                    <use href="../assets/bootstrap-italia/dist/svg/sprites.svg#it-error"></use>
                  </svg>
                  <div>
                    <h5 className="alert-heading">Si è verificato un errore</h5>
                    <p className="mb-0">{callbackData.error}</p>
                  </div>
                  <button 
                    className="btn btn-danger mt-3" 
                    onClick={handleBackToLogin}
                  >
                    <svg className="icon icon-white me-2" aria-hidden="true">
                      <use href="../assets/bootstrap-italia/dist/svg/sprites.svg#it-arrow-left"></use>
                    </svg>
                    Torna al login
                  </button>
                </div>
              )}
              
              {/* Successo */}
              {callbackData.user && !callbackData.error && (
                <div className="alert alert-success d-flex align-items-center" role="alert">
                  <svg className="icon icon-success me-2" aria-hidden="true">
                    <use href="../assets/bootstrap-italia/dist/svg/sprites.svg#it-check-circle"></use>
                  </svg>
                  <div>
                    <h5 className="alert-heading">Autenticazione completata!</h5>
                    <p className="mb-0">Sarai reindirizzato automaticamente...</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Debug Panel - visibile solo in development */}
          {process.env.NODE_ENV === 'development' && (
            <div className="card mt-4">
              <div className="card-header bg-primary text-white">
                <h2 className="h5 mb-0">Dati di Debug</h2>
              </div>
              <div className="card-body">
                <div className="accordion" id="debugAccordion">
                  <div className="accordion-item">
                    <h3 className="accordion-header">
                      <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#urlParams">
                        Parametri URL
                      </button>
                    </h3>
                    <div id="urlParams" className="accordion-collapse collapse">
                      <div className="accordion-body">
                        <pre className="bg-light p-3 mb-0">
                          {JSON.stringify(callbackData.urlParams, null, 2)}
                        </pre>
                      </div>
                    </div>
                  </div>
                  
                  {callbackData.tokenResponse && (
                    <div className="accordion-item">
                      <h3 className="accordion-header">
                        <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#tokenData">
                          Dati Token
                        </button>
                      </h3>
                      <div id="tokenData" className="accordion-collapse collapse">
                        <div className="accordion-body">
                          <pre className="bg-light p-3 mb-0">
                            {JSON.stringify(callbackData.tokenResponse, null, 2)}
                          </pre>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
} 